import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Rocket, Plus, SearchX, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Sidebar } from "@/components/Sidebar";
import { TopHeader } from "@/components/TopHeader";
import { SortableAppCard } from "@/components/SortableAppCard";
import { AppCard } from "@/components/AppCard";
import { AppFormDialog } from "@/components/AppFormDialog";
import { WebView } from "@/components/WebView";
import { UsersDialog } from "@/components/UsersDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import * as apiLib from "@/lib/api";

export default function Launcher() {
  const { user, signOut } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeApp, setActiveApp] = useState(null);
  const [usersOpen, setUsersOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    try {
      const data = await apiLib.fetchApps();
      setApps(data);
    } catch (e) {
      toast.error("Failed to load apps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const categories = useMemo(
    () => Array.from(new Set(apps.map((a) => a.category).filter(Boolean))).sort(),
    [apps]
  );

  const counts = useMemo(() => {
    const c = { all: apps.length, favorites: apps.filter((a) => a.favorite).length, categories: {} };
    categories.forEach((cat) => { c.categories[cat] = apps.filter((a) => a.category === cat).length; });
    return c;
  }, [apps, categories]);

  const filtered = useMemo(() => {
    let list = apps;
    if (filter === "favorites") list = list.filter((a) => a.favorite);
    else if (filter.startsWith("cat:")) list = list.filter((a) => a.category === filter.slice(4));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((a) =>
      a.name.toLowerCase().includes(q) || a.url.toLowerCase().includes(q) || (a.category || "").toLowerCase().includes(q)
    );
    return list;
  }, [apps, filter, search]);

  const title = filter === "all" ? "All Apps"
    : filter === "favorites" ? "Favorites"
    : filter.startsWith("cat:") ? filter.slice(4) : "Apps";

  const reorderable = filter === "all" && !search.trim();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = apps.findIndex((a) => a.id === active.id);
    const newIndex = apps.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(apps, oldIndex, newIndex);
    setApps(next);
    apiLib.reorderApps(next.map((a) => a.id)).catch(() => {
      toast.error("Failed to save order");
      load();
    });
  };

  const handleLaunch = (app) => {
    if (app.open_mode === "browser") {
      window.open(app.url, "_blank", "noopener,noreferrer");
      toast.success(`Opening ${app.name} in browser`);
    } else {
      setActiveApp(app);
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiLib.updateApp(editing.id, data);
        toast.success("App updated");
      } else {
        await apiLib.createApp(data);
        toast.success("App added");
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save app");
    }
  };

  const handleToggleFavorite = async (app) => {
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, favorite: !a.favorite } : a)));
    try { await apiLib.toggleFavorite(app.id); }
    catch (e) { toast.error("Failed to update favorite"); load(); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiLib.deleteApp(deleteTarget.id);
      toast.success("App removed");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error("Could not delete app"); }
  };

  if (activeApp) {
    return <WebView app={activeApp} onBack={() => setActiveApp(null)} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        categories={categories}
        activeFilter={filter}
        onFilter={setFilter}
        counts={counts}
        onAddApp={() => { setEditing(null); setFormOpen(true); }}
        user={user}
        onManageUsers={() => setUsersOpen(true)}
        onChangePassword={() => setPwOpen(true)}
        onLogout={signOut}
      />

      <div className="ml-64">
        <TopHeader search={search} onSearch={setSearch} title={title} />

        <main className="p-8">
          {loading ? (
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
              {search || filter !== "all" ? (
                <>
                  <SearchX className="h-16 w-16 text-muted-foreground/40" />
                  <p className="mt-4 text-lg font-semibold">No apps found</p>
                  <p className="text-sm text-muted-foreground">Try a different search or category.</p>
                </>
              ) : (
                <>
                  <Rocket className="h-16 w-16 text-muted-foreground/40" />
                  <p className="mt-4 text-lg font-semibold">Your launcher is empty</p>
                  <p className="text-sm text-muted-foreground">Add your first web app to get started.</p>
                  <button onClick={() => { setEditing(null); setFormOpen(true); }}
                    data-testid="empty-add-app"
                    className="mt-6 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow transition-transform hover:brightness-110 active:scale-95">
                    <Plus className="h-4 w-4" /> Add App
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {reorderable && (
                <p className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="reorder-hint">
                  <GripVertical className="h-3.5 w-3.5" /> Drag the handle on a card to rearrange your apps
                </p>
              )}
              {reorderable ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={filtered.map((a) => a.id)} strategy={rectSortingStrategy}>
                    <div
                      className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 lg:gap-8 xl:grid-cols-6"
                      data-testid="app-grid"
                    >
                      {filtered.map((app) => (
                        <SortableAppCard
                          key={app.id}
                          app={app}
                          onLaunch={handleLaunch}
                          onEdit={(a) => { setEditing(a); setFormOpen(true); }}
                          onDelete={setDeleteTarget}
                          onToggleFavorite={handleToggleFavorite}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div
                  className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 lg:gap-8 xl:grid-cols-6"
                  data-testid="app-grid"
                >
                  {filtered.map((app) => (
                    <AppCard
                      key={app.id}
                      app={app}
                      onLaunch={handleLaunch}
                      onEdit={(a) => { setEditing(a); setFormOpen(true); }}
                      onDelete={setDeleteTarget}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <AppFormDialog
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        onSubmit={handleSubmit}
        initial={editing}
        categories={categories}
      />

      <UsersDialog open={usersOpen} onOpenChange={setUsersOpen} currentUser={user} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the app from your launcher. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
